'use client'

import { useEffect, useState } from 'react'
import { BlogCard } from '@/components/BlogCard'
import type { BlogPost } from '@/lib/types'
import { db } from '@/lib/firebase'
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore'
import { FiSearch, FiFilter, FiBookOpen, FiGrid, FiList, FiAlertTriangle, FiRefreshCw, FiWifi } from 'react-icons/fi'

const CATEGORIES = [
  { name: 'All',              slug: 'all' },
  { name: 'Health Tips',      slug: 'health-tips' },
  { name: 'Medical Insights', slug: 'medical-insights' },
  { name: 'Wellness',         slug: 'wellness' },
  { name: 'Practice Updates', slug: 'practice-updates' },
]

export default function BlogPage() {
  const [posts, setPosts]                       = useState<BlogPost[]>([])
  const [loading, setLoading]                   = useState(true)
  const [error, setError]                       = useState<string | null>(null)
  const [retryCount, setRetryCount]             = useState(0)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery]           = useState('')
  const [viewMode, setViewMode]                 = useState<'grid' | 'list'>('grid')

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true)
      setError(null)
      try {
        const postsRef = collection(db, 'posts')
        const q = query(postsRef, where('published', '==', true), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        const fetchedPosts: BlogPost[] = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            title: data.title || '',
            slug: data.slug || '',
            excerpt: data.excerpt || '',
            content: data.content || '',
            category: data.category || 'Health Tips',
            author: data.author || 'Dr. Nthabiseng Matla',
            authorImage: data.authorImage || '',
            publishedAt: data.publishedAt?.toDate
              ? data.publishedAt.toDate().toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0],
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : '',
            featuredImage: data.featuredImage || '',
            readTime: data.readTime || 5,
            tags: data.tags || [],
          }
        })
        setPosts(fetchedPosts)
      } catch (err: unknown) {
        console.error('Error fetching posts:', err)
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
        if (isOffline) {
          setError('offline')
        } else if (err instanceof Error && err.message.toLowerCase().includes('permission')) {
          setError('permission')
        } else {
          setError('unknown')
        }
        setPosts([])
      } finally {
        setLoading(false)
      }
    }
    fetchPosts()
  }, [retryCount])

  const handleRetry = () => setRetryCount((c) => c + 1)

  const filteredPosts = posts.filter((post) => {
    const matchesCategory =
      selectedCategory === 'all' ||
      post.category.toLowerCase().replace(/ /g, '-') === selectedCategory ||
      post.category === selectedCategory
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  return (
    <>
      <style>{`
        @keyframes blogGrad {
          0%,100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        @keyframes blogFloat {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          50%      { transform: translateY(-18px) rotate(3deg); }
        }
        @keyframes blogFloatB {
          0%,100% { transform: translateY(0px); }
          50%      { transform: translateY(-12px) rotate(-2deg); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .blog-hero-bg {
          background: linear-gradient(135deg,#0F766E 0%,#0D9488 38%,#06B6D4 72%,#0e7490 100%);
          background-size: 400% 400%;
          animation: blogGrad 8s ease infinite;
        }
        .blog-nl-bg {
          background: linear-gradient(135deg,#0F766E 0%,#0D9488 38%,#06B6D4 72%,#0e7490 100%);
          background-size: 400% 400%;
          animation: blogGrad 8s ease infinite;
        }
        .bfa { animation: blogFloat  6s ease-in-out infinite; }
        .bfb { animation: blogFloatB 5s ease-in-out 1s infinite; }
        .bfc { animation: blogFloat  7s ease-in-out 2.5s infinite; }
        .shimmer-blog {
          background: linear-gradient(90deg,#0F766E 0%,#06B6D4 45%,#0F766E 90%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }
        .css-spinner {
          width: 48px; height: 48px;
          border: 4px solid rgba(15,118,110,0.2);
          border-top-color: #0F766E;
          border-radius: 50%;
          animation: spin 0.9s linear infinite;
        }
      `}</style>

      <div className="min-h-screen bg-white overflow-hidden">

        {/* HERO */}
        <section className="blog-hero-bg relative py-28 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="bfa absolute top-10  right-[14%] w-36 h-36 rounded-full bg-white/10 backdrop-blur-sm border border-white/20" />
            <div className="bfb absolute bottom-12 left-[9%]  rounded-full bg-cyan-300/20 border border-white/15" style={{ width: '88px', height: '88px' }} />
            <div className="bfc absolute top-1/3 left-[32%] w-14 h-14 rounded-full bg-teal-200/15 border border-white/10" />
            <div className="bfa absolute top-1/4 right-[36%] w-8  h-8  rounded-full bg-white/20" style={{ animationDelay: '3s' }} />
          </div>
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 bg-white/20 text-white text-sm font-semibold px-4 py-2 rounded-full mb-6 backdrop-blur-sm border border-white/30">
                <FiBookOpen size={14} /> Health Blog &amp; Insights
              </span>
              <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-5">
                Expert Health Tips &amp;<br />
                <span className="text-cyan-200">Medical Insights</span>
              </h1>
              <p className="text-lg text-teal-100 leading-relaxed max-w-lg">
                Stay informed with the latest health articles, wellness tips, and medical insights from Dr. Nthabiseng Matla
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 1440 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 56 C360 0 1080 0 1440 56 L1440 56 L0 56Z" fill="white" />
            </svg>
          </div>
        </section>

        {/* SEARCH & FILTER */}
        <div className="max-w-7xl mx-auto px-6 -mt-8 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl outline-none focus:border-teal-500 focus:bg-white transition-all"
                />
              </div>
              <div className="hidden lg:flex items-center gap-2">
                <FiFilter className="text-gray-400" size={18} />
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setSelectedCategory(cat.slug)}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                      selectedCategory === cat.slug
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow text-teal-600' : 'text-gray-400'}`}
                >
                  <FiGrid size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow text-teal-600' : 'text-gray-400'}`}
                >
                  <FiList size={18} />
                </button>
              </div>
            </div>
            <div className="lg:hidden mt-4 flex gap-2 overflow-x-auto pb-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-all ${
                    selectedCategory === cat.slug ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* POSTS */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="css-spinner" />
              </div>
            ) : error ? (
              <div className="text-center py-20 max-w-md mx-auto">
                <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
                  error === 'offline' ? 'bg-amber-50' : 'bg-red-50'
                }`}>
                  {error === 'offline'
                    ? <FiWifi className="text-amber-400" size={40} />
                    : <FiAlertTriangle className="text-red-400" size={40} />}
                </div>
                <h3 className="text-2xl font-extrabold text-gray-800 mb-3">
                  {error === 'offline'
                    ? 'You appear to be offline'
                    : error === 'permission'
                    ? 'Access denied'
                    : 'Failed to load articles'}
                </h3>
                <p className="text-gray-500 mb-8 leading-relaxed">
                  {error === 'offline'
                    ? 'Please check your internet connection and try again.'
                    : error === 'permission'
                    ? 'You do not have permission to view these posts. Please contact support if this persists.'
                    : 'Something went wrong while fetching articles. Our team has been notified.'}
                </p>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-500 transition-colors"
                >
                  <FiRefreshCw size={16} /> Try Again
                </button>
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className={viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
                : 'space-y-6'}>
                {filteredPosts.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <FiBookOpen className="text-gray-400" size={40} />
                </div>
                <h3 className="text-2xl font-extrabold text-gray-700 mb-2">No posts found</h3>
                <p className="text-gray-500">
                  {searchQuery
                    ? `No results for "${searchQuery}". Try a different search term.`
                    : 'Check back soon for new health articles and insights.'}
                </p>
              </div>
            )}

            {filteredPosts.length >= 6 && (
              <div className="text-center mt-12">
                <button className="px-8 py-4 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors">
                  Load More Articles
                </button>
              </div>
            )}
          </div>
        </section>

        {/* NEWSLETTER */}
        <section className="blog-nl-bg py-20 relative overflow-hidden">
          <div className="bfa absolute -top-12 -right-12 w-64 h-64 rounded-full bg-white/5 pointer-events-none" />
          <div className="bfb absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-cyan-300/10 pointer-events-none" />
          <div className="max-w-2xl mx-auto px-6 text-center relative z-10">
            <h2 className="text-3xl font-extrabold text-white mb-3">Subscribe to Our Newsletter</h2>
            <p className="text-teal-100 mb-8">
              Get the latest health tips and articles delivered directly to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-5 py-4 rounded-xl outline-none text-gray-900 font-medium"
              />
              <button className="px-7 py-4 bg-white/20 border border-white/40 text-white font-semibold rounded-xl hover:bg-white/30 transition-all backdrop-blur-sm">
                Subscribe
              </button>
            </div>
          </div>
        </section>

      </div>
    </>
  )
}
